import type { ReportFilter } from '../types/reporting';

// ---------------------------------------------------------------------------
// Shared per-filter bound primitives
// ---------------------------------------------------------------------------
//
// Extracted from useReportRunner so the gap-fill date spine (compileReport) can
// reuse the SAME per-filter bound semantics without inheriting
// deriveExtractScope's COLUMN SELECTION. The two consumers must be free to
// disagree about which columns they look at:
//
//   - deriveExtractScope only considers primary, non-derived columns the server
//     advertises as prunable (`prunable_axes`), because a window on any other
//     axis mints a distinct cache entry holding identical bytes.
//   - the spine considers whichever column the report groups by (dotted refs
//     included), because "which days exist" is a question about the query, not
//     about what the server can prune.
//
// Only the pure primitives are shared. Nothing here knows about datasets,
// manifests, prunability, or derivation.

export interface Bound {
  value: string;
  inclusive: boolean;
}

export interface AxisConstraint {
  lower?: Bound;
  upper?: Bound;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// Only a well-formed ISO date is usable as a bound. Anything else (a mid-edit
// blank, a number on a retyped column) yields NO bound, which fails open to a
// full fetch — the safe direction.
export const isoBound = (value: unknown): string | null =>
  typeof value === 'string' && ISO_DATE.test(value.trim())
    ? value.trim()
    : null;

// The axis constraint a single filter contributes, or null when the filter says
// nothing prunable. Ops that only NARROW the result further (neq, in, contains,
// not_null) deliberately contribute nothing: ignoring them over-serves, and
// over-serving is safe.
//
// That "over-serving is safe" reasoning holds for extract pruning but NOT for
// row fabrication, so the spine caller must additionally REFUSE to build a
// window when any such op constrains its grouped column (`between Jan 1-10 AND
// neq Jan 5` must not re-add Jan 5 as a $0 row the user's own filter excludes).
// This function stays deliberately blind to them; the refusal lives in the
// caller, where it is auditable.
export function boundsOf(filter: ReportFilter): AxisConstraint | null {
  const scalar = (): string | null => isoBound(filter.value);
  switch (filter.op) {
    case 'between': {
      const bounds = filter.value;
      if (!Array.isArray(bounds)) return null;
      const lower = isoBound(bounds[0]);
      const upper = isoBound(bounds[1]);
      if (lower === null || upper === null) return null;
      return {
        lower: { value: lower, inclusive: true },
        upper: { value: upper, inclusive: true },
      };
    }
    case 'eq': {
      // A single day, encoded as a degenerate inclusive range — it prunes
      // identically to `kind=equality` under the server's min/max overlap test.
      const value = scalar();
      return value === null
        ? null
        : {
            lower: { value, inclusive: true },
            upper: { value, inclusive: true },
          };
    }
    case 'gte':
    case 'gt': {
      const value = scalar();
      return value === null
        ? null
        : { lower: { value, inclusive: filter.op === 'gte' } };
    }
    case 'lte':
    case 'lt': {
      const value = scalar();
      return value === null
        ? null
        : { upper: { value, inclusive: filter.op === 'lte' } };
    }
    default:
      return null;
  }
}

// Filters are AND-ed in the compiled WHERE, so two constraints on one axis
// intersect: keep the LATER lower bound and the EARLIER upper bound. On a tie
// the exclusive bound wins (it is the tighter of the two).
export const tighterLower = (a: Bound | undefined, b: Bound): Bound => {
  if (!a) return b;
  if (b.value > a.value) return b;
  if (b.value < a.value) return a;
  return a.inclusive ? b : a;
};

export const tighterUpper = (a: Bound | undefined, b: Bound): Bound => {
  if (!a) return b;
  if (b.value < a.value) return b;
  if (b.value > a.value) return a;
  return a.inclusive ? b : a;
};
